import { NotificationType, useLorebook, useTranslation } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonVariant, IconVariants, Modal, Spinner, SpinnerSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Lorebook, LorebookVisibility, PostLorebookRequest } from "@next-platform/packages-ts-types";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag, useToast } from "@/hooks";
import { buildLorebookImportRequest, createEmptyFormValues, mapImportedLorebookToFormValues } from "@/utils/importHelpers";

import LorebookForm, { ILorebookSubmitError, TabNames } from "./components/LorebookForm";
import SelectionCard from "./components/SelectionCard";
import { FORM_FIELDS } from "./constants";
import { ILorebookDataFormProps, ILorebookEditNavigationState } from "./types";

const LOREBOOK_GUIDE_KEY = "lorebook_guide_seen";
const DOCUMENTATION_LINK = "https://docs.spicychat.ai/product-guides/lorebook";

const parseJsonFile = async (file: File): Promise<unknown> => {
  const text = await file.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Invalid JSON file. Please upload a valid lorebook export.");
  }
};

const extractLorebookId = (responseRecord: Record<string, unknown>): string | null => {
  if (typeof responseRecord.id === "string") return responseRecord.id;
  if (typeof responseRecord.lorebookId === "string") return responseRecord.lorebookId;
  return null;
};

const resetFileInput = (event: ChangeEvent<HTMLInputElement>) => {
  if (event.target) {
    event.target.value = "";
  }
};

export default function LorebookCreate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { onUserCreateLorebook, onUserImportLorebook, getLorebookData } = useLorebook();
  const { displayToast } = useToast();
  const { hasFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_IMPORT);
  const isLorebookImportAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.LorebookImport);

  const [isSelectionMode, setIsSelectionMode] = useState(true);
  const [isGuideModalVisible, setIsGuideModalVisible] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState<ILorebookDataFormProps>(createEmptyFormValues());
  const [formKey, setFormKey] = useState(() => `lorebook-form-${Date.now()}`);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldWarnOnNavigate, setShouldWarnOnNavigate] = useState(false);
  // The API moderates the text fields even though create always sends Private, so an underage
  // rejection lands here (NX-11368). Create only submits the details form, hence the fixed tab tag.
  const [stateSubmitError, setStateSubmitError] = useState<ILorebookSubmitError | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isSelectionMode) {
      const hasSeenGuide = localStorage.getItem(LOREBOOK_GUIDE_KEY);
      if (!hasSeenGuide) {
        setIsGuideModalVisible(true);
      }
    }
  }, [isSelectionMode]);

  const handleGuideModalContinue = useCallback(() => {
    setIsGuideModalVisible(false);
  }, []);

  const handleStartNew = useCallback(() => {
    setFormInitialValues(createEmptyFormValues());
    setFormKey(`lorebook-form-${Date.now()}`);
    setIsSelectionMode(false);
    setShouldWarnOnNavigate(false);
  }, []);

  const handleImportClick = useCallback(() => {
    if (isProcessingUpload) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }, [isProcessingUpload]);

  const tryNavigateToLorebook = useCallback(
    async (lorebookId: string): Promise<boolean> => {
      try {
        const lorebookResult = await getLorebookData(lorebookId);
        if (lorebookResult.data) {
          displayToast({ message: "Lorebook Imported", type: NotificationType.success });
          navigate(ROUTES.lorebookEdit.replace(":id", lorebookId));
          return true;
        }
      } catch {
        // Import succeeded but failed to navigate to edit page; fall through to form display
      }
      return false;
    },
    [getLorebookData, displayToast, navigate],
  );

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsProcessingUpload(true);
      setIsUploadModalVisible(true);

      try {
        const parsed = await parseJsonFile(file);
        const { request: importRequest, fallbackFormValues } = buildLorebookImportRequest(parsed);

        let formValues: ILorebookDataFormProps | null = fallbackFormValues ?? null;
        let importErrorMessage: string | null = null;
        let importedLorebookId: string | null = null;

        const importResult = await onUserImportLorebook(importRequest).catch(
          (apiError: unknown) =>
            // eslint-disable-next-line id-denylist -- property name matches onUserImportLorebook return shape
            ({ data: null, error: apiError instanceof Error ? apiError.message : "Failed to import lorebook." }) as const,
        );

        const importPayload = importResult["data"];
        const importError = importResult.error;

        if (importPayload && !importError) {
          formValues = mapImportedLorebookToFormValues(importPayload as Lorebook | Record<string, unknown>, fallbackFormValues);
          importedLorebookId = extractLorebookId(importPayload as unknown as Record<string, unknown>);
        } else if (importError) {
          importErrorMessage = importError;
        }

        if (!formValues) {
          throw new Error(importErrorMessage || "Failed to import lorebook.");
        }

        if (importedLorebookId && (await tryNavigateToLorebook(importedLorebookId))) {
          return;
        }

        setFormInitialValues(formValues);
        setFormKey(`lorebook-form-${Date.now()}`);
        setIsSelectionMode(false);
        setShouldWarnOnNavigate(true);
      } catch (fileImportError) {
        const message = fileImportError instanceof Error ? fileImportError.message : t("lorebook:landing.import.error.generic");
        displayToast({ message: message, type: NotificationType.error });
      } finally {
        setIsProcessingUpload(false);
        setIsUploadModalVisible(false);
        resetFileInput(event);
      }
    },
    [onUserImportLorebook, tryNavigateToLorebook, displayToast, t],
  );

  const handleSubmit = useCallback(
    async (formData: FieldValues) => {
      setIsLoading(true);
      setStateSubmitError(null);
      try {
        // A lorebook is always created as Private: a Public lorebook requires at least one entry and
        // must pass review, which only happens later in the Edit/Entries flow via "Submit Lorebook for
        // Review". The user's Public selection is honored there, not at create time.
        const payload = {
          name: formData.name,
          description: formData.description,
          tags: formData.tags,
          avatar_url: formData.avatar_url,
          visibility: LorebookVisibility.PRIVATE,
        } as PostLorebookRequest;
        const response = await onUserCreateLorebook(payload);
        const createdLorebookId = response.data ? extractLorebookId(response.data as unknown as Record<string, unknown>) : null;
        if (createdLorebookId) {
          displayToast({ message: t("lorebook:toast.created"), type: NotificationType.success });
          // Carry the user's selected visibility into the Edit/Entries flow so the form pre-selects it
          // (the lorebook itself was created Private) and shows the matching submit CTA.
          const navigationState: ILorebookEditNavigationState = { intendedVisibility: formData[FORM_FIELDS.VISIBILITY] };
          navigate(ROUTES.lorebookEditEntries.replace(":id", createdLorebookId), { state: navigationState });
        } else if (response.error) {
          setIsLoading(false);
          setStateSubmitError({ message: response.error, tab: TabNames.LOREBOOK });
        } else {
          // Create returned a payload without a usable id; do not navigate to an "undefined" route.
          setIsLoading(false);
          setStateSubmitError({ message: t("lorebook:landing.import.error.generic"), tab: TabNames.LOREBOOK });
        }
      } catch (submitError) {
        setIsLoading(false);
        throw submitError;
      }
    },
    [onUserCreateLorebook, displayToast, navigate, t],
  );

  const handleUploadModalOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && !isProcessingUpload) {
        setIsUploadModalVisible(false);
      }
    },
    [isProcessingUpload],
  );

  const handleGuideModalOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      localStorage.setItem(LOREBOOK_GUIDE_KEY, "true");
    }
    setIsGuideModalVisible(isOpen);
  }, []);

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      void handleFileChange(event);
    },
    [handleFileChange],
  );

  const onFormSubmit = useCallback(
    (formData: FieldValues) => {
      handleSubmit(formData);
    },
    [handleSubmit],
  );

  const clearSubmitError = useCallback(() => setStateSubmitError(null), []);

  const uploadModalClassNames = useMemo(() => ({ content: "flex flex-col gap-4 items-center" }), []);
  const guideModalTitleClassNames = useMemo(() => ({ title: "text-center" }), []);

  return (
    <>
      <input ref={fileInputRef} type="file" accept="application/json,.json" hidden onChange={onFileInputChange} />
      <div className="flex flex-col gap-lg">
        {isSelectionMode ? (
          <PageLayout size={PageSize.Small}>
            <PageLayout.Header title={t("lorebook:title.create")} />
            <PageLayout.Content classNames="flex flex-col gap-5">
              <SelectionCard
                label={t("lorebook:landing.action.startNew.title")}
                subLabel={t("lorebook:landing.action.startNew.subtitle")}
                icon={IconVariants.EDIT}
                onClick={handleStartNew}
              />
              {!!hasFeatureFlag && isLorebookImportAllowed ? (
                <SelectionCard
                  data-testid="LorebookImportSelectionCard"
                  label={t("lorebook:landing.action.import.title")}
                  subLabel={t("lorebook:landing.action.import.subtitle")}
                  icon={IconVariants.UPLOAD}
                  onClick={handleImportClick}
                />
              ) : null}
            </PageLayout.Content>
          </PageLayout>
        ) : (
          <LorebookForm
            key={formKey}
            onSubmit={onFormSubmit}
            isLoading={isLoading}
            initialValues={formInitialValues}
            initiallyDirty={shouldWarnOnNavigate}
            submitError={stateSubmitError}
            onClearSubmitError={clearSubmitError}
          />
        )}
      </div>

      <Modal open={isUploadModalVisible} onOpenChange={handleUploadModalOpenChange} title="Importing Lorebook" classNames={uploadModalClassNames}>
        <Spinner size={SpinnerSize.SMALL} />
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-center text-gray-12">
          <Trans i18nKey="lorebook:landing.import.processing" />
        </Text>
      </Modal>

      <Modal
        open={isGuideModalVisible}
        onOpenChange={handleGuideModalOpenChange}
        title={t("lorebook:modal.onboarding.title")}
        classNames={guideModalTitleClassNames}
      >
        <div className="flex flex-col gap-4">
          <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
            <Trans i18nKey="lorebook:modal.onboarding.message" />{" "}
            <a
              href={DOCUMENTATION_LINK}
              target="_blank"
              aria-label="lorebook-guide"
              className="text-blue-10 underline hover:underline-none transition-colors"
            >
              <Trans i18nKey="lorebook:modal.onboarding.linkLabel" />
            </a>{" "}
            <Trans i18nKey="lorebook:modal.onboarding.postLinkLabel" />
          </Text>

          <Button variant={ButtonVariant.CONTAINED} onClick={handleGuideModalContinue}>
            <Trans i18nKey="common:continue" />
          </Button>
        </div>
      </Modal>
    </>
  );
}
